import { env } from '@/src/config/env'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'

const RELEASE_DATE = new Date(env.EXPO_PUBLIC_PRAYER_SPACE_V2_RELEASE_DATE)

/**
 * Percentage of *newly registered* users (registered on or after EXPO_PUBLIC_PRAYER_SPACE_V2_RELEASE_DATE)
 * shown the new prayer space. Configured via Cloudflare env var
 * EXPO_PUBLIC_PRAYER_SPACE_V2_NEW_USERS_PERCENT.
 * Defaults to 0 (rollout disabled) when unset — classic variant is always shown.
 */
const NEW_USERS_PERCENT = Number(env.EXPO_PUBLIC_PRAYER_SPACE_V2_NEW_USERS_PERCENT ?? 0)

/**
 * Percentage of *existing* users (registered before EXPO_PUBLIC_PRAYER_SPACE_V2_RELEASE_DATE)
 * shown the new prayer space. Configured via Cloudflare env var
 * EXPO_PUBLIC_PRAYER_SPACE_V2_EXISTING_USERS_PERCENT.
 * Defaults to 0 (rollout disabled) when unset — classic variant is always shown.
 */
const EXISTING_USERS_PERCENT = Number(env.EXPO_PUBLIC_PRAYER_SPACE_V2_EXISTING_USERS_PERCENT ?? 0)

type TPrayerSpaceV2 = {
  userId: number
  shouldShowNewPrayerSpace: boolean
  /** user.id % 10, stable bucket identifier for this user (0–9) */
  userBucket: number
  /** true if the user registered on or after RELEASE_DATE */
  isNewlyRegisteredUser: boolean
  /** rollout threshold applied to this user's bucket */
  rolloutPercent: number
}

export const usePrayerSpaceV2 = (): TPrayerSpaceV2 | null => {
  const { data: me } = useMeQuery()

  if (!me) return null

  const isNewlyRegisteredUser = new Date(me.subscribedAt) >= RELEASE_DATE

  if (me.admin) {
    return {
      userId: me.id,
      shouldShowNewPrayerSpace: true,
      userBucket: 0,
      isNewlyRegisteredUser,
      rolloutPercent: 100,
    }
  }

  const userBucket = me.id % 10
  const rolloutPercent = isNewlyRegisteredUser ? NEW_USERS_PERCENT : EXISTING_USERS_PERCENT

  return {
    userId: me.id,
    // userBucket is 0-9, rolloutPercent is a 0-100 percentage, so scale it down to bucket count
    shouldShowNewPrayerSpace: userBucket < rolloutPercent / 10,
    userBucket,
    isNewlyRegisteredUser,
    rolloutPercent,
  }
}
